/**
 * Settings › Treatment Templates (Figma: board 7151:4088, frame 4).
 *
 * A dentist's own saved treatment plans: create, edit, set or clear the
 * favourite for a condition, and delete. Templates hold procedures only,
 * never medicines, and are applied to whichever tooth has the condition.
 * The backend owns ownership: every call acts on the signed-in doctor's
 * templates only.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import {
  CONDITION_BY_KEY,
  DENTAL_CONDITIONS,
  PROCEDURES,
  PROCEDURE_BY_CODE,
  type ConditionKey,
  type ProcedureCode,
} from "../../../constants/dental.constants";
import { DoctorTemplateAPI, type DoctorTemplate } from "../../../services/doctor-template.service";
import type { TemplateItem } from "../../../utils/dental-plan";

const MAX_ITEMS = 30;

interface Draft {
  id: string | null;
  condition: ConditionKey;
  name: string;
  items: TemplateItem[];
  favourite: boolean;
}

const emptyDraft = (): Draft => ({ id: null, condition: "caries", name: "", items: [], favourite: true });

const errorText = (e: unknown, fallback: string) => (e instanceof Error && e.message ? e.message : fallback);

const TreatmentTemplates: React.FC = () => {
  const [rows, setRows] = useState<DoctorTemplate[] | null>(null);
  const [error, setError] = useState<string | null>(null);
  const [draft, setDraft] = useState<Draft | null>(null);
  const [busyId, setBusyId] = useState<string | null>(null);
  const [confirmDelete, setConfirmDelete] = useState<string | null>(null);

  const load = useCallback(async () => {
    try {
      setRows(await DoctorTemplateAPI.list());
      setError(null);
    } catch (e) {
      setError(errorText(e, "Could not load your templates. Check your connection and try again."));
    }
  }, []);

  useEffect(() => { void load(); }, [load]);

  const sorted = useMemo(
    () => [...(rows ?? [])].sort((a, b) =>
      (a.condition_key ?? "").localeCompare(b.condition_key ?? "") || Number(b.is_favourite) - Number(a.is_favourite) || a.name.localeCompare(b.name)),
    [rows],
  );

  const run = async (id: string, action: () => Promise<unknown>, fail: string) => {
    setBusyId(id);
    setError(null);
    try {
      await action();
      await load();
    } catch (e) {
      setError(errorText(e, fail));
    } finally {
      setBusyId(null);
    }
  };

  const toggleFavourite = (t: DoctorTemplate) =>
    run(t.template_id, () => DoctorTemplateAPI.update(t.template_id, { is_favourite: !t.is_favourite }), "Could not change the favourite.");

  const remove = (t: DoctorTemplate) =>
    run(t.template_id, () => DoctorTemplateAPI.remove(t.template_id), "Could not delete the template.").then(() => setConfirmDelete(null));

  const edit = (t: DoctorTemplate) => setDraft({
    id: t.template_id,
    condition: (t.condition_key ?? "caries") as ConditionKey,
    name: t.name,
    items: t.payload.items,
    favourite: t.is_favourite,
  });

  return (
    <div className="flex flex-col gap-4">
      <div className="flex flex-wrap items-start justify-between gap-3 rounded-2xl border border-slate-100 bg-white p-5 shadow-sm">
        <div>
          <h2 className="text-lg font-bold text-slate-900">Treatment templates</h2>
          <p className="text-sm text-slate-500">Your own saved plans. Only you can see or use them.</p>
        </div>
        <button type="button" onClick={() => setDraft(emptyDraft())}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white hover:bg-sky-700">
          + New template
        </button>
      </div>

      {error && <p role="alert" className="rounded-lg bg-red-50 px-4 py-2 text-sm text-red-700">{error}</p>}

      {draft && (
        <TemplateEditor
          draft={draft}
          existing={rows ?? []}
          onCancel={() => setDraft(null)}
          onSaved={async () => { setDraft(null); await load(); }}
        />
      )}

      <div className="overflow-x-auto rounded-2xl border border-slate-100 bg-white shadow-sm">
        {rows === null && !error ? (
          <p className="p-5 text-sm text-slate-500">Loading your templates…</p>
        ) : sorted.length === 0 ? (
          <div className="p-6 text-sm text-slate-500">
            <p className="font-semibold text-slate-700">No templates yet.</p>
            <p>Save one from the treatment plan on the Diagnosis Pad, or create one here with “New template”.</p>
          </div>
        ) : (
          <table className="w-full min-w-[720px] text-left text-sm">
            <thead className="bg-slate-50 text-[11px] font-semibold uppercase tracking-wide text-slate-500">
              <tr>
                <th className="px-4 py-2">Condition</th>
                <th className="px-4 py-2">Name</th>
                <th className="px-4 py-2">Procedures</th>
                <th className="px-4 py-2">Favourite</th>
                <th className="px-4 py-2"><span className="sr-only">Actions</span></th>
              </tr>
            </thead>
            <tbody>
              {sorted.map((t) => {
                const busy = busyId === t.template_id;
                return (
                  <tr key={t.template_id} className="border-t border-slate-100 align-top">
                    <td className="px-4 py-3 text-slate-800">
                      {t.condition_key ? CONDITION_BY_KEY[t.condition_key as ConditionKey]?.label ?? t.condition_key : "Any"}
                    </td>
                    <td className="px-4 py-3 font-semibold text-slate-900">{t.name}</td>
                    <td className="px-4 py-3 text-xs text-slate-600">
                      {t.payload.items.map((i) => `${i.label}${i.sittings_planned > 1 ? ` (${i.sittings_planned})` : ""}`).join(", ")}
                    </td>
                    <td className="px-4 py-3">
                      <button type="button" disabled={busy || !t.condition_key} onClick={() => toggleFavourite(t)}
                        aria-pressed={t.is_favourite}
                        className={`rounded-full px-2.5 py-1 text-xs disabled:opacity-40 ${t.is_favourite ? "bg-amber-50 font-semibold text-amber-700" : "text-sky-700 hover:bg-sky-50"}`}>
                        {t.is_favourite ? "★ Favourite" : "☆ Set"}
                      </button>
                    </td>
                    <td className="px-4 py-3">
                      {confirmDelete === t.template_id ? (
                        <span className="flex items-center gap-2 text-xs">
                          <span className="text-slate-600">Delete?</span>
                          <button type="button" disabled={busy} onClick={() => remove(t)} className="font-semibold text-red-600">Delete</button>
                          <button type="button" onClick={() => setConfirmDelete(null)} className="text-slate-500">Keep</button>
                        </span>
                      ) : (
                        <span className="flex gap-3 text-xs">
                          <button type="button" disabled={busy} onClick={() => edit(t)} className="text-sky-700">Edit</button>
                          <button type="button" disabled={busy} onClick={() => setConfirmDelete(t.template_id)} className="text-red-600">Delete</button>
                        </span>
                      )}
                    </td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        )}
      </div>
      <p className="text-xs text-slate-500">One favourite per condition: setting a new one replaces the old. Templates hold procedures only, never medicines.</p>
    </div>
  );
};

// ─── Create / edit ────────────────────────────────────────────────────────────

const TemplateEditor: React.FC<{
  draft: Draft;
  existing: DoctorTemplate[];
  onCancel: () => void;
  onSaved: () => Promise<void>;
}> = ({ draft: initial, existing, onCancel, onSaved }) => {
  const [d, setD] = useState<Draft>(initial);
  const [add, setAdd] = useState<ProcedureCode>("iopa");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const isNew = d.id === null;

  const currentFavourite = existing.find((t) => t.is_favourite && t.condition_key === d.condition && t.template_id !== d.id);

  const addItem = () => {
    if (d.items.some((i) => i.procedure_code === add) || d.items.length >= MAX_ITEMS) return;
    const p = PROCEDURE_BY_CODE[add];
    setD({ ...d, items: [...d.items, { procedure_code: p.code, label: p.label, sittings_planned: p.sittings, scope: p.scope }] });
  };
  const setSittings = (code: string, n: number) =>
    setD({ ...d, items: d.items.map((i) => (i.procedure_code === code ? { ...i, sittings_planned: Math.min(20, Math.max(1, n)) } : i)) });
  const removeItem = (code: string) => setD({ ...d, items: d.items.filter((i) => i.procedure_code !== code) });

  const valid = d.name.trim().length > 0 && d.items.length > 0;

  const save = async () => {
    if (!valid) return;
    setBusy(true);
    setError(null);
    try {
      const payload = { items: d.items };
      if (isNew) {
        await DoctorTemplateAPI.create({ kind: "dental_plan", condition_key: d.condition, name: d.name.trim(), payload, is_favourite: d.favourite });
      } else {
        await DoctorTemplateAPI.update(d.id as string, { name: d.name.trim(), payload, is_favourite: d.favourite });
      }
      await onSaved();
    } catch (e) {
      setError(errorText(e, "Could not save the template. Check your connection and try again."));
    } finally {
      setBusy(false);
    }
  };

  return (
    <div className="flex flex-col gap-4 rounded-2xl border border-sky-200 bg-white p-5 shadow-sm">
      <h3 className="text-base font-bold text-slate-900">{isNew ? "New template" : "Edit template"}</h3>
      <div className="flex flex-wrap gap-3">
        <div className="flex min-w-[200px] flex-1 flex-col gap-1 text-xs font-semibold text-slate-600">
          <label htmlFor="tpl-condition">Condition</label>
          <select id="tpl-condition" value={d.condition} disabled={!isNew}
            aria-describedby={isNew ? undefined : "tpl-condition-help"}
            onChange={(e) => setD({ ...d, condition: e.target.value as ConditionKey })}
            className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-normal text-slate-800 disabled:opacity-60">
            {DENTAL_CONDITIONS.map((c) => <option key={c.key} value={c.key}>{c.label}</option>)}
          </select>
          {!isNew && <span id="tpl-condition-help" className="font-normal text-slate-500">To use another condition, create a new template.</span>}
        </div>
        <label htmlFor="tpl-name" className="flex min-w-[200px] flex-1 flex-col gap-1 text-xs font-semibold text-slate-600">
          Name
          <input id="tpl-name" value={d.name} maxLength={120} placeholder="My RCT plan"
            onChange={(e) => setD({ ...d, name: e.target.value })}
            className="rounded-lg bg-slate-100 px-3 py-2 text-sm font-normal text-slate-800" />
        </label>
      </div>

      <div className="rounded-xl border border-slate-200">
        <p className="bg-slate-50 px-3 py-2 text-[11px] font-semibold uppercase tracking-wide text-slate-500">Procedures</p>
        {d.items.length === 0 && <p className="px-3 py-2 text-xs text-slate-500">Add at least one procedure.</p>}
        {d.items.map((i) => (
          <div key={i.procedure_code} className="flex items-center gap-3 border-t border-slate-100 px-3 py-2 text-sm">
            <span className="flex-1">{i.label}</span>
            <span className="inline-flex items-center gap-1.5 text-xs text-slate-600">
              <button type="button" aria-label={`Fewer sittings for ${i.label}`} onClick={() => setSittings(i.procedure_code, i.sittings_planned - 1)}
                className="h-5 w-5 rounded bg-slate-100">−</button>
              {i.sittings_planned} sitting{i.sittings_planned === 1 ? "" : "s"}
              <button type="button" aria-label={`More sittings for ${i.label}`} onClick={() => setSittings(i.procedure_code, i.sittings_planned + 1)}
                className="h-5 w-5 rounded bg-slate-100">+</button>
            </span>
            <button type="button" aria-label={`Remove ${i.label}`} onClick={() => removeItem(i.procedure_code)} className="px-1 text-slate-400">×</button>
          </div>
        ))}
        <div className="flex flex-wrap items-center gap-2 border-t border-slate-100 px-3 py-2">
          <label htmlFor="tpl-add" className="sr-only">Procedure to add</label>
          <select id="tpl-add" value={add} onChange={(e) => setAdd(e.target.value as ProcedureCode)}
            className="min-w-[200px] flex-1 rounded-lg bg-slate-100 px-2 py-1.5 text-sm">
            {PROCEDURES.map((p) => <option key={p.code} value={p.code}>{p.label}</option>)}
          </select>
          <button type="button" onClick={addItem} disabled={d.items.some((i) => i.procedure_code === add) || d.items.length >= MAX_ITEMS}
            className="rounded-lg border border-slate-200 px-3 py-1.5 text-sm disabled:opacity-40">Add</button>
        </div>
      </div>

      <label htmlFor="tpl-favourite" className="flex items-start gap-2 text-sm text-slate-700">
        <input id="tpl-favourite" type="checkbox" checked={d.favourite} onChange={(e) => setD({ ...d, favourite: e.target.checked })} className="mt-0.5" />
        <span>
          Favourite: apply automatically when this condition is charted.
          {d.favourite && currentFavourite && <span className="block text-xs text-amber-700">Replaces “{currentFavourite.name}” as the favourite.</span>}
        </span>
      </label>

      {error && <p role="alert" className="rounded-lg bg-red-50 px-3 py-2 text-xs text-red-700">{error}</p>}

      <div className="flex justify-end gap-2">
        <button type="button" onClick={onCancel} className="rounded-lg border border-slate-200 px-4 py-2 text-sm text-slate-700">Cancel</button>
        <button type="button" onClick={save} disabled={busy || !valid}
          className="rounded-lg bg-sky-600 px-4 py-2 text-sm font-semibold text-white disabled:opacity-40">
          {busy ? "Saving" : isNew ? "Create" : "Save changes"}
        </button>
      </div>
    </div>
  );
};

export default TreatmentTemplates;
